/**
 * Shared SemanticChartData -> Chart.js dataset mapping.
 *
 * Single implementation used by SemanticChart (calculator/comparison/glossary
 * education blocks) and BlogChart (blog in-body charts) so every surface
 * renders identical visuals from the same contract. Colors resolve through
 * the single-source-of-truth palette — no raw hex in content files.
 */
import type { ChartData } from "chart.js";
import {
  CHART_COLORS,
  CHART_SERIES,
  chartColorWithAlpha,
} from "@/lib/constants/chartPalette";
import type { SemanticChartColor, SemanticChartData } from "@/lib/charts/types";

/** Semantic data keys → canvas hex from the single-source-of-truth palette. */
const SEMANTIC_COLORS: Record<SemanticChartColor, string> = {
  primary: CHART_COLORS.primary,
  secondary: CHART_COLORS.secondary,
  orange: CHART_COLORS.accentOrange,
  danger: CHART_COLORS.danger,
  cyan: CHART_COLORS.accentCyan,
  violet: CHART_COLORS.violet,
  blue: CHART_COLORS.blue,
};

function seriesColor(color: SemanticChartColor | undefined, index: number): string {
  if (color && SEMANTIC_COLORS[color]) return SEMANTIC_COLORS[color];
  return CHART_SERIES[index % CHART_SERIES.length];
}

export function buildSemanticChartData(
  chartData: SemanticChartData
): ChartData<"pie" | "doughnut" | "line" | "bar"> {
  const { type, labels, datasets } = chartData;

  return {
    labels,
    datasets: datasets.map((ds, i) => {
      const base = seriesColor(ds.color, i);

      if (type === "doughnut") {
        // Doughnut slices each need their own categorical color
        const sliceColors = labels.map((_, si) => CHART_SERIES[si % CHART_SERIES.length]);
        return {
          label: ds.label,
          data: ds.data,
          backgroundColor: sliceColors.map((c) => chartColorWithAlpha(c, 0.8)),
          borderColor: sliceColors,
          borderWidth: 2,
        };
      }

      if (type === "line") {
        return {
          label: ds.label,
          data: ds.data,
          borderColor: base,
          backgroundColor: chartColorWithAlpha(base, 0.15),
          pointBackgroundColor: base,
          borderWidth: 2,
          tension: 0.3,
          fill: true,
        };
      }

      return {
        label: ds.label,
        data: ds.data,
        backgroundColor: chartColorWithAlpha(base, 0.8),
        borderColor: base,
        borderWidth: 1,
        borderRadius: 4,
      };
    }),
  };
}
